/**
 * WhatsApp Admin Page (OpenWA Notification Engine)
 * ================================================
 *
 * Single super-admin surface for the whole platform's WhatsApp pipeline.
 * Four tabs:
 *   1. Settings — global default + per-tenant overrides
 *   2. Templates — 16 pre-seeded + custom
 *   3. Logs — sent/failed/skipped + manual retry button
 *   4. Test Send — quick "did my config work?" check
 *
 * Mounted at /admin/whatsapp (super-admin only).
 *
 * Backend contract: /api/admin/whatsapp/{settings,templates,logs,test}
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { MessageCircle, Settings, FileText, ScrollText, Send, Plus, Trash2, Edit3, Loader2, RefreshCw, ArrowLeft, ExternalLink, CheckCircle2, AlertCircle, Clock, Megaphone, Users, BarChart3, TrendingDown, ShieldCheck } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = async (path, opts = {}) => {
  const r = await fetch(`${API_URL}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opts.headers || {}) },
  });
  if (!r.ok) {
    const e = await r.json().catch(() => ({}));
    throw new Error(e.detail || `Request failed (${r.status})`);
  }
  return r.json();
};

const STATUS_CLR = {
  sent: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  failed: 'bg-rose-50 text-rose-700 border-rose-200',
  skipped: 'bg-slate-50 text-slate-500 border-slate-200',
  queued: 'bg-sky-50 text-sky-700 border-sky-200',
  delivered: 'bg-emerald-100 text-emerald-800 border-emerald-300',
};

export default function WhatsAppAdminPage() {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="whatsapp-admin-page">
      <header className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-3">
          <Link to="/admin" className="text-slate-400 hover:text-slate-700"><ArrowLeft className="h-5 w-5" /></Link>
          <div>
            <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-emerald-600" />
              WhatsApp (OpenWA) Notification Engine
            </h1>
            <p className="text-xs text-slate-500">Central pipeline for every module — settings, templates, logs and retries.</p>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        <Tabs defaultValue="dashboard">
          <TabsList>
            <TabsTrigger value="dashboard" data-testid="wa-tab-dashboard"><BarChart3 className="h-3.5 w-3.5 mr-1" />Dashboard</TabsTrigger>
            <TabsTrigger value="settings" data-testid="wa-tab-settings"><Settings className="h-3.5 w-3.5 mr-1" />Settings</TabsTrigger>
            <TabsTrigger value="templates" data-testid="wa-tab-templates"><FileText className="h-3.5 w-3.5 mr-1" />Templates</TabsTrigger>
            <TabsTrigger value="broadcasts" data-testid="wa-tab-broadcasts"><Megaphone className="h-3.5 w-3.5 mr-1" />Broadcasts</TabsTrigger>
            <TabsTrigger value="logs" data-testid="wa-tab-logs"><ScrollText className="h-3.5 w-3.5 mr-1" />Logs</TabsTrigger>
            <TabsTrigger value="test" data-testid="wa-tab-test"><Send className="h-3.5 w-3.5 mr-1" />Test Send</TabsTrigger>
          </TabsList>
          <TabsContent value="dashboard" className="mt-4"><DashboardTab /></TabsContent>
          <TabsContent value="settings" className="mt-4"><SettingsTab /></TabsContent>
          <TabsContent value="templates" className="mt-4"><TemplatesTab /></TabsContent>
          <TabsContent value="broadcasts" className="mt-4"><BroadcastsTab /></TabsContent>
          <TabsContent value="logs" className="mt-4"><LogsTab /></TabsContent>
          <TabsContent value="test" className="mt-4"><TestSendTab /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

// ============== SETTINGS ==============

function SettingsTab() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['wa-settings'],
    queryFn: () => api('/api/admin/whatsapp/settings'),
  });
  const settings = data?.settings || [];

  const [editing, setEditing] = useState(null);
  const del = useMutation({
    mutationFn: (tenant_id) => api(`/api/admin/whatsapp/settings/${tenant_id || 'global'}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Removed'); qc.invalidateQueries({ queryKey: ['wa-settings'] }); },
    onError: (e) => toast.error(e.message),
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base">OpenWA Settings</CardTitle>
            <p className="text-xs text-slate-500 mt-1">A global row applies platform-wide; tenant overrides take precedence when set.</p>
          </div>
          <Button size="sm" onClick={() => setEditing({})} data-testid="wa-settings-add"><Plus className="h-4 w-4 mr-1" />New row</Button>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 text-center"><Loader2 className="h-4 w-4 animate-spin inline" /></div>
          ) : settings.length === 0 ? (
            <div className="p-10 text-center text-slate-400">
              <p className="mb-2">No OpenWA settings configured yet.</p>
              <Button size="sm" onClick={() => setEditing({ tenant_id: null })} data-testid="wa-settings-create-default">Create global default</Button>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="text-[10px] uppercase tracking-wide text-slate-500 border-b">
                <th className="text-left py-2 px-3">Scope</th>
                <th className="text-left py-2 px-3">Base URL</th>
                <th className="text-left py-2 px-3">Send path</th>
                <th className="text-left py-2 px-3">API Key</th>
                <th className="text-left py-2 px-3">Session</th>
                <th className="text-left py-2 px-3">Active</th>
                <th className="text-right py-2 px-3"></th>
              </tr></thead>
              <tbody>
                {settings.map(s => (
                  <tr key={s.id} className="border-b last:border-0" data-testid={`wa-setting-${s.id}`}>
                    <td className="py-2 px-3">
                      {s.tenant_id ? (
                        <Badge variant="outline" className="text-[10px]">tenant: {s.tenant_id.slice(0, 8)}…</Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">GLOBAL</Badge>
                      )}
                    </td>
                    <td className="py-2 px-3 text-xs font-mono">{s.base_url}</td>
                    <td className="py-2 px-3 text-xs font-mono">{s.send_text_path}</td>
                    <td className="py-2 px-3 text-xs font-mono">{s.api_key_masked || <span className="text-slate-300">—</span>}</td>
                    <td className="py-2 px-3 text-xs">{s.default_session || '—'}</td>
                    <td className="py-2 px-3">
                      {s.active ? <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Active</Badge> : <Badge className="bg-slate-100 text-slate-500 text-[10px]">Inactive</Badge>}
                    </td>
                    <td className="py-2 px-3 text-right">
                      {s.dashboard_url && <a href={s.dashboard_url} target="_blank" rel="noreferrer" className="inline-block mr-1"><Button variant="ghost" size="icon" className="h-7 w-7" title="Open dashboard"><ExternalLink className="h-3 w-3" /></Button></a>}
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditing(s)} data-testid={`wa-setting-edit-${s.id}`}><Edit3 className="h-3 w-3" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-600" onClick={() => { if (window.confirm('Delete this settings row?')) del.mutate(s.tenant_id); }} data-testid={`wa-setting-del-${s.id}`}><Trash2 className="h-3 w-3" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {editing && <SettingsDialog row={editing} onClose={() => setEditing(null)} onSaved={() => { qc.invalidateQueries({ queryKey: ['wa-settings'] }); setEditing(null); }} />}
    </div>
  );
}

function SettingsDialog({ row, onClose, onSaved }) {
  const [form, setForm] = useState({
    tenant_id: row?.tenant_id ?? null,
    base_url: row?.base_url || 'http://localhost:2785/api',
    send_text_path: row?.send_text_path || '/sendText',
    api_key: '',
    api_key_header: row?.api_key_header || 'api_key',
    default_session: row?.default_session || '',
    active: row?.active ?? true,
    dashboard_url: row?.dashboard_url || 'http://localhost:2886',
    timeout_seconds: row?.timeout_seconds || 15,
  });
  const mut = useMutation({
    mutationFn: () => api('/api/admin/whatsapp/settings', { method: 'PUT', body: JSON.stringify(form) }),
    onSuccess: () => { toast.success('Saved'); onSaved?.(); },
    onError: (e) => toast.error(e.message),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="wa-settings-dialog">
        <DialogHeader>
          <DialogTitle>{row?.id ? 'Edit OpenWA settings' : 'New OpenWA settings'}</DialogTitle>
          <DialogDescription>Leave tenant blank for the global default. API key is write-only (blank = keep existing).</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-auto">
          <div><Label>Tenant ID (leave blank for global)</Label>
            <Input value={form.tenant_id || ''} onChange={e => setForm({ ...form, tenant_id: e.target.value || null })} placeholder="leave blank → GLOBAL default" data-testid="wa-form-tenant" />
          </div>
          <div><Label>Base URL *</Label>
            <Input value={form.base_url} onChange={e => setForm({ ...form, base_url: e.target.value })} placeholder="http://your-openwa-host:2785/api" data-testid="wa-form-base-url" />
            <p className="text-[10px] text-slate-400 mt-0.5">Must be reachable from the backend server (not localhost when deployed)</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Send-text path</Label>
              <Input value={form.send_text_path} onChange={e => setForm({ ...form, send_text_path: e.target.value })} placeholder="/sendText" data-testid="wa-form-path" />
            </div>
            <div><Label>Dashboard URL</Label>
              <Input value={form.dashboard_url} onChange={e => setForm({ ...form, dashboard_url: e.target.value })} placeholder="http://your-host:2886" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>API key header</Label>
              <Input value={form.api_key_header} onChange={e => setForm({ ...form, api_key_header: e.target.value })} placeholder="api_key or X-API-Key" />
            </div>
            <div><Label>API key (write-only)</Label>
              <Input type="password" value={form.api_key} onChange={e => setForm({ ...form, api_key: e.target.value })} placeholder={row?.api_key_masked || 'leave blank to keep existing'} data-testid="wa-form-api-key" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Default session</Label>
              <Input value={form.default_session} onChange={e => setForm({ ...form, default_session: e.target.value })} placeholder="main / sessionId" />
            </div>
            <div><Label>Timeout (seconds)</Label>
              <Input type="number" min={5} max={120} value={form.timeout_seconds} onChange={e => setForm({ ...form, timeout_seconds: parseInt(e.target.value, 10) || 15 })} />
            </div>
          </div>
          <div className="flex items-center gap-2 pt-2">
            <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} id="wa-form-active" data-testid="wa-form-active" />
            <Label htmlFor="wa-form-active">Active</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mut.mutate()} disabled={!form.base_url || mut.isPending} data-testid="wa-settings-save">
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============== TEMPLATES ==============

function TemplatesTab() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['wa-templates'],
    queryFn: () => api('/api/admin/whatsapp/templates'),
  });
  const templates = data?.templates || [];
  const [editing, setEditing] = useState(null);
  const del = useMutation({
    mutationFn: (id) => api(`/api/admin/whatsapp/templates/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Deleted'); qc.invalidateQueries({ queryKey: ['wa-templates'] }); },
    onError: (e) => toast.error(e.message),
  });
  const reseed = useMutation({
    mutationFn: () => api('/api/admin/whatsapp/templates/seed', { method: 'POST' }),
    onSuccess: (r) => { toast.success(`Seeded ${r.inserted} missing default(s)`); qc.invalidateQueries({ queryKey: ['wa-templates'] }); },
    onError: (e) => toast.error(e.message),
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base">Message Templates ({templates.length})</CardTitle>
            <p className="text-xs text-slate-500 mt-1">Variables use <code className="text-[10px] bg-slate-100 px-1 rounded">{'{name}'}</code> style. Missing vars render as empty.</p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => reseed.mutate()} disabled={reseed.isPending} data-testid="wa-templates-reseed">
              {reseed.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Re-seed defaults
            </Button>
            <Button size="sm" onClick={() => setEditing({})} data-testid="wa-templates-add"><Plus className="h-4 w-4 mr-1" />New template</Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 text-center"><Loader2 className="h-4 w-4 animate-spin inline" /></div>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="text-[10px] uppercase tracking-wide text-slate-500 border-b">
                <th className="text-left py-2 px-3">Key</th>
                <th className="text-left py-2 px-3">Name</th>
                <th className="text-left py-2 px-3">Variables</th>
                <th className="text-right py-2 px-3"></th>
              </tr></thead>
              <tbody>
                {templates.map(t => (
                  <tr key={t.id} className="border-b last:border-0" data-testid={`wa-template-${t.key}`}>
                    <td className="py-2 px-3 font-mono text-xs">{t.key}{t.is_default && <span className="ml-1 text-[9px] text-slate-400">DEFAULT</span>}</td>
                    <td className="py-2 px-3">{t.name}</td>
                    <td className="py-2 px-3">
                      <div className="flex flex-wrap gap-1">
                        {(t.variables || []).slice(0, 5).map(v => <span key={v} className="text-[9px] px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded font-mono">{v}</span>)}
                        {(t.variables || []).length > 5 && <span className="text-[9px] text-slate-400">+{t.variables.length - 5}</span>}
                      </div>
                    </td>
                    <td className="py-2 px-3 text-right">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditing(t)} data-testid={`wa-template-edit-${t.key}`}><Edit3 className="h-3 w-3" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-600" onClick={() => { if (window.confirm(`Delete template "${t.name}"?`)) del.mutate(t.id); }} data-testid={`wa-template-del-${t.key}`}><Trash2 className="h-3 w-3" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {editing && <TemplateDialog tpl={editing} onClose={() => setEditing(null)} onSaved={() => { qc.invalidateQueries({ queryKey: ['wa-templates'] }); setEditing(null); }} />}
    </div>
  );
}

function TemplateDialog({ tpl, onClose, onSaved }) {
  const editing = !!tpl?.id;
  const [form, setForm] = useState({
    key: tpl?.key || '',
    name: tpl?.name || '',
    body: tpl?.body || '',
    variables: (tpl?.variables || []).join(', '),
    description: tpl?.description || '',
  });
  const mut = useMutation({
    mutationFn: () => {
      const body = JSON.stringify({
        ...form,
        variables: form.variables.split(',').map(s => s.trim()).filter(Boolean),
      });
      return editing
        ? api(`/api/admin/whatsapp/templates/${tpl.id}`, { method: 'PUT', body })
        : api('/api/admin/whatsapp/templates', { method: 'POST', body });
    },
    onSuccess: () => { toast.success(editing ? 'Updated' : 'Created'); onSaved?.(); },
    onError: (e) => toast.error(e.message),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="wa-template-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit template' : 'New template'}</DialogTitle>
          <DialogDescription>Reference variables in the body as <code className="text-[10px] bg-slate-100 px-1 rounded">{'{name}'}</code>.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Key *</Label><Input value={form.key} onChange={e => setForm({ ...form, key: e.target.value })} disabled={editing} placeholder="payment_receipt" data-testid="wa-tpl-key" /></div>
            <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Payment receipt" data-testid="wa-tpl-name" /></div>
          </div>
          <div><Label>Body *</Label><Textarea rows={5} value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} placeholder="Hi {name}, thank you for {amount}…" data-testid="wa-tpl-body" /></div>
          <div><Label>Variables <span className="text-[10px] text-slate-400">(comma-separated)</span></Label>
            <Input value={form.variables} onChange={e => setForm({ ...form, variables: e.target.value })} placeholder="name, amount, currency" />
          </div>
          <div><Label>Description</Label><Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mut.mutate()} disabled={!form.key || !form.name || !form.body || mut.isPending} data-testid="wa-tpl-save">
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============== LOGS ==============

function LogsTab() {
  const qc = useQueryClient();
  const [filters, setFilters] = useState({ status: '', module: '', recipient: '' });
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.module) params.set('module', filters.module);
  if (filters.recipient) params.set('recipient', filters.recipient);
  params.set('limit', '100');

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['wa-logs', filters],
    queryFn: () => api(`/api/admin/whatsapp/logs?${params.toString()}`),
  });
  const logs = data?.logs || [];

  const retry = useMutation({
    mutationFn: (id) => api(`/api/admin/whatsapp/logs/${id}/retry`, { method: 'POST' }),
    onSuccess: (r) => { toast[r.success ? 'success' : 'warning'](r.success ? 'Retried successfully' : (r.error || 'Retry failed')); qc.invalidateQueries({ queryKey: ['wa-logs'] }); },
    onError: (e) => toast.error(e.message),
  });

  return (
    <div className="space-y-4">
      <OptOutsCard />
      <div className="flex flex-wrap gap-2 items-end">
        <div className="w-40"><Label className="text-xs">Status</Label>
          <Select value={filters.status || 'all'} onValueChange={(v) => setFilters({ ...filters, status: v === 'all' ? '' : v })}>
            <SelectTrigger data-testid="wa-logs-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="skipped">Skipped</SelectItem>
              <SelectItem value="queued">Queued</SelectItem>
              <SelectItem value="delivered">Delivered</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-40"><Label className="text-xs">Module</Label>
          <Input value={filters.module} onChange={e => setFilters({ ...filters, module: e.target.value })} placeholder="church / events…" />
        </div>
        <div className="w-56"><Label className="text-xs">Recipient</Label>
          <Input value={filters.recipient} onChange={e => setFilters({ ...filters, recipient: e.target.value })} placeholder="phone partial" />
        </div>
        <Button size="sm" variant="outline" onClick={() => refetch()} data-testid="wa-logs-refresh"><RefreshCw className="h-4 w-4" /></Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 text-center"><Loader2 className="h-4 w-4 animate-spin inline" /></div>
          ) : logs.length === 0 ? (
            <p className="p-10 text-center text-sm text-slate-400">No logs match these filters. Try a Test Send to seed one.</p>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="text-[10px] uppercase tracking-wide text-slate-500 border-b">
                <th className="text-left py-2 px-3">Time</th>
                <th className="text-left py-2 px-3">Status</th>
                <th className="text-left py-2 px-3">Recipient</th>
                <th className="text-left py-2 px-3">Module</th>
                <th className="text-left py-2 px-3">Type</th>
                <th className="text-left py-2 px-3">Error</th>
                <th className="text-right py-2 px-3">Retry</th>
              </tr></thead>
              <tbody>
                {logs.map(l => (
                  <tr key={l.id} className="border-b last:border-0" data-testid={`wa-log-${l.id}`}>
                    <td className="py-2 px-3 text-[10px] text-slate-500 whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</td>
                    <td className="py-2 px-3"><Badge variant="outline" className={`text-[10px] capitalize ${STATUS_CLR[l.status] || ''}`}>{l.status}</Badge></td>
                    <td className="py-2 px-3 text-xs">
                      <p>{l.recipient}</p>
                      {l.recipient_name && <p className="text-[10px] text-slate-400">{l.recipient_name}</p>}
                    </td>
                    <td className="py-2 px-3 text-xs">{l.module || '—'}</td>
                    <td className="py-2 px-3 text-xs">{l.message_type || l.template_key || '—'}</td>
                    <td className="py-2 px-3 text-[10px] text-rose-600 max-w-xs truncate" title={l.error || ''}>{l.error || ''}</td>
                    <td className="py-2 px-3 text-right whitespace-nowrap">
                      {l.status === 'failed' && (
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => retry.mutate(l.id)} disabled={retry.isPending} data-testid={`wa-log-retry-${l.id}`}>
                          <RefreshCw className="h-3 w-3 mr-1" />Retry ({l.retry_count || 0}/{l.max_retries || 5})
                        </Button>
                      )}
                      {l.status === 'sent' && <CheckCircle2 className="h-4 w-4 text-emerald-600 inline" />}
                      {l.status === 'skipped' && <Clock className="h-4 w-4 text-slate-400 inline" />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ============== TEST SEND ==============

/** Human-friendly label from a snake_case variable: "campaign_name" → "Campaign name" */
const humanizeVar = (v) => (v || '').replace(/_/g, ' ').replace(/\b\w/, c => c.toUpperCase());

/** Live-preview the template body by substituting {var} with the value the
 *  admin has typed. Missing values render as the placeholder "{var}" itself
 *  so the preview shows exactly which fields still need filling.
 */
function renderTemplatePreview(body, context) {
  if (!body) return '';
  return body.replace(/\{(\w+)\}/g, (_, k) => {
    const v = context[k];
    return (v === undefined || v === null || v === '') ? `{${k}}` : String(v);
  });
}

/** Reusable amateur-friendly template context editor.
 *  Props:
 *   - template:   the loaded template doc ({key, name, body, variables}) or null
 *   - context:    {var: value} dict
 *   - onChange:   (newContext) => void
 *   - testIdBase: data-testid prefix (e.g. 'wa-test' or 'wa-broadcast')
 *   - autoFilledKeys: array of variable names that callers fill server-side
 *                     (e.g. 'name' on broadcasts) — UI shows a friendly hint
 *                     and disables the input.
 */
function TemplateContextFields({ template, context, onChange, testIdBase = 'wa-ctx', autoFilledKeys = [] }) {
  const [showJson, setShowJson] = useState(false);
  const [jsonText, setJsonText] = useState(JSON.stringify(context, null, 2));
  const variables = template?.variables || [];

  // Keep the JSON editor in sync when the field-based state changes
  React.useEffect(() => { setJsonText(JSON.stringify(context, null, 2)); }, [context]);

  if (!template) return null;
  if (variables.length === 0) {
    return <p className="text-xs text-slate-400 italic">This template has no variables — nothing to fill in.</p>;
  }

  return (
    <div className="space-y-2 border rounded-lg bg-slate-50 p-3" data-testid={`${testIdBase}-fields`}>
      <div className="flex items-center justify-between">
        <Label className="text-xs">Fill in the template values</Label>
        <button
          type="button"
          onClick={() => setShowJson(s => !s)}
          className="text-[10px] text-slate-500 hover:text-slate-700 underline"
          data-testid={`${testIdBase}-toggle-json`}
        >
          {showJson ? '← Back to simple form' : 'Edit as JSON (advanced)'}
        </button>
      </div>

      {showJson ? (
        <div>
          <Textarea
            rows={6} className="font-mono text-xs"
            value={jsonText}
            onChange={e => {
              setJsonText(e.target.value);
              try { onChange(JSON.parse(e.target.value)); } catch { /* keep typing */ }
            }}
            data-testid={`${testIdBase}-json`}
          />
          <p className="text-[10px] text-slate-400 mt-1">Tip: switch back to the simple form if you're not sure about JSON syntax.</p>
        </div>
      ) : (
        <div className="grid gap-2">
          {variables.map(v => {
            const auto = autoFilledKeys.includes(v);
            return (
              <div key={v}>
                <Label className="text-xs flex items-center gap-1">
                  {humanizeVar(v)}
                  {auto && <span className="text-[9px] text-emerald-600 bg-emerald-50 px-1 rounded">auto-filled per recipient</span>}
                </Label>
                <Input
                  value={auto ? '' : (context[v] ?? '')}
                  placeholder={auto ? `Filled from each recipient's name` : `e.g. ${humanizeVar(v)}`}
                  disabled={auto}
                  onChange={e => onChange({ ...context, [v]: e.target.value })}
                  data-testid={`${testIdBase}-${v}`}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TestSendTab() {
  const [form, setForm] = useState({ to: '', body: 'This is a test WhatsApp message from the platform Notification Engine.', template_key: '' });
  const [context, setContext] = useState({});
  const [result, setResult] = useState(null);
  const { data: tplData } = useQuery({
    queryKey: ['wa-templates'],
    queryFn: () => api('/api/admin/whatsapp/templates'),
  });
  const templates = tplData?.templates || [];
  const selectedTemplate = templates.find(t => t.key === form.template_key) || null;

  // Reset context when template changes so leftover fields don't bleed in
  React.useEffect(() => {
    if (!selectedTemplate) { setContext({}); return; }
    const fresh = {};
    (selectedTemplate.variables || []).forEach(v => { fresh[v] = context[v] || ''; });
    setContext(fresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.template_key]);

  const livePreview = selectedTemplate ? renderTemplatePreview(selectedTemplate.body, context) : '';

  const mut = useMutation({
    mutationFn: () => {
      const body = { to: form.to, body: form.template_key ? '' : form.body };
      if (form.template_key) { body.template_key = form.template_key; body.context = context; }
      return api('/api/admin/whatsapp/test', { method: 'POST', body: JSON.stringify(body) });
    },
    onSuccess: (r) => { setResult(r); toast[r.status === 'sent' ? 'success' : 'warning'](`Test ${r.status}` + (r.error ? `: ${r.error}` : '')); },
    onError: (e) => toast.error(e.message),
  });

  return (
    <div className="grid md:grid-cols-2 gap-4">
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Send className="h-4 w-4 text-emerald-600" />Send Test WhatsApp Message</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div><Label>Recipient phone (E.164) *</Label><Input value={form.to} onChange={e => setForm({ ...form, to: e.target.value })} placeholder="+27821234567" data-testid="wa-test-to" /></div>
          <div><Label>Template (optional)</Label>
            <Select value={form.template_key || 'none'} onValueChange={(v) => setForm({ ...form, template_key: v === 'none' ? '' : v })}>
              <SelectTrigger data-testid="wa-test-template"><SelectValue placeholder="Send as raw body" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— None (use raw body) —</SelectItem>
                {templates.map(t => <SelectItem key={t.key} value={t.key}>{t.name || t.key}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {form.template_key ? (
            <TemplateContextFields template={selectedTemplate} context={context} onChange={setContext} testIdBase="wa-test" />
          ) : (
            <div><Label>Message body</Label>
              <Textarea rows={5} value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} data-testid="wa-test-body" />
            </div>
          )}
          {form.template_key && (
            <div className="rounded-lg border bg-white p-3" data-testid="wa-test-preview">
              <p className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Live preview</p>
              <p className="text-sm whitespace-pre-wrap text-slate-700">{livePreview}</p>
            </div>
          )}
          <Button onClick={() => mut.mutate()} disabled={!form.to || mut.isPending} data-testid="wa-test-send">
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}<Send className="h-4 w-4 mr-1" />Send Test WhatsApp Message
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Result</CardTitle></CardHeader>
        <CardContent>
          {result ? (
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                {result.status === 'sent' && <CheckCircle2 className="h-5 w-5 text-emerald-600" />}
                {result.status === 'failed' && <AlertCircle className="h-5 w-5 text-rose-600" />}
                {result.status === 'skipped' && <Clock className="h-5 w-5 text-slate-400" />}
                <span className="font-medium capitalize">{result.status}</span>
              </div>
              {result.error && <p className="text-xs text-rose-600">{result.error}</p>}
              {result.message_id && <p className="text-xs text-slate-500">Message ID: <code>{result.message_id}</code></p>}
              {result.log_id && <p className="text-xs text-slate-500">Log ID: <code>{result.log_id}</code></p>}
            </div>
          ) : (
            <p className="text-sm text-slate-400">No test sent yet.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


// ============== BROADCASTS ==============

const AUDIENCE_TYPES = [
  { value: 'custom', label: 'Custom phone list', needs_id: false },
  { value: 'church_members', label: 'Church members', needs_id: true, hint: 'Church id' },
  { value: 'event_delegates', label: 'Event delegates', needs_id: true, hint: 'Event id' },
  { value: 'school_class', label: 'School class (parents)', needs_id: true, hint: 'Class id' },
  { value: 'school_staff', label: 'School staff', needs_id: true, hint: 'School tenant id' },
  { value: 'school_students', label: 'School students (guardian phones)', needs_id: true, hint: 'School tenant id' },
  { value: 'school_parents', label: 'School parents (all)', needs_id: true, hint: 'School tenant id' },
];

function BroadcastsTab() {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    audience_type: 'custom', audience_id: '', template_key: '', body: '',
    extra_phones: '', title: 'Broadcast',
  });
  const [context, setContext] = useState({});
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const { data: tplData } = useQuery({
    queryKey: ['wa-templates'],
    queryFn: () => api('/api/admin/whatsapp/templates'),
  });
  const templates = tplData?.templates || [];
  const selectedTemplate = templates.find(t => t.key === form.template_key) || null;

  // Reset context when template changes
  React.useEffect(() => {
    if (!selectedTemplate) { setContext({}); return; }
    const fresh = {};
    (selectedTemplate.variables || []).forEach(v => { fresh[v] = context[v] || ''; });
    setContext(fresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.template_key]);

  const { data: bData, isLoading: bLoading } = useQuery({
    queryKey: ['wa-broadcasts'],
    queryFn: () => api('/api/admin/whatsapp/broadcasts'),
  });
  const broadcasts = bData?.broadcasts || [];
  const aud = AUDIENCE_TYPES.find(a => a.value === form.audience_type) || AUDIENCE_TYPES[0];

  const previewMut = useMutation({
    mutationFn: () => api('/api/admin/whatsapp/broadcasts/audience/preview', {
      method: 'POST',
      body: JSON.stringify({
        audience_type: form.audience_type,
        audience_id: form.audience_id,
        extra_phones: form.extra_phones.split(',').map(s => s.trim()).filter(Boolean),
      }),
    }),
    onSuccess: (r) => setPreview(r),
    onError: (e) => toast.error(e.message),
  });

  const sendMut = useMutation({
    mutationFn: () => {
      const body = {
        audience_type: form.audience_type,
        audience_id: form.audience_id,
        template_key: form.template_key || null,
        body: form.template_key ? null : form.body,
        context: form.template_key ? context : null,
        extra_phones: form.extra_phones.split(',').map(s => s.trim()).filter(Boolean),
        title: form.title,
      };
      return api('/api/admin/whatsapp/broadcasts', { method: 'POST', body: JSON.stringify(body) });
    },
    onSuccess: (r) => {
      setResult(r); qc.invalidateQueries({ queryKey: ['wa-broadcasts'] });
      toast.success(`Broadcast complete: ${r.sent} sent · ${r.failed} failed · ${r.skipped} skipped`);
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="wa-broadcasts-tab">
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Megaphone className="h-4 w-4 text-emerald-600" />Compose Broadcast</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div><Label>Title</Label>
            <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="wa-broadcast-title" />
          </div>
          <div><Label>Audience *</Label>
            <Select value={form.audience_type} onValueChange={(v) => setForm({ ...form, audience_type: v })}>
              <SelectTrigger data-testid="wa-broadcast-audience-type"><SelectValue /></SelectTrigger>
              <SelectContent>{AUDIENCE_TYPES.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {aud.needs_id && (
            <div><Label>{aud.hint} *</Label>
              <Input value={form.audience_id} onChange={e => setForm({ ...form, audience_id: e.target.value })} placeholder={aud.hint} data-testid="wa-broadcast-audience-id" />
            </div>
          )}
          <div><Label>Extra phones <span className="text-[10px] text-slate-400">(comma-separated, always included)</span></Label>
            <Input value={form.extra_phones} onChange={e => setForm({ ...form, extra_phones: e.target.value })} placeholder="+27 82…, +27 83…" data-testid="wa-broadcast-extra-phones" />
          </div>
          <Button variant="outline" size="sm" onClick={() => previewMut.mutate()} disabled={previewMut.isPending} data-testid="wa-broadcast-preview">
            {previewMut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}<Users className="h-3.5 w-3.5 mr-1" />Preview audience
          </Button>
          {preview && (
            <div className="text-xs bg-slate-50 border rounded p-2">
              <p className="font-medium">{preview.count} recipient{preview.count === 1 ? '' : 's'}</p>
              {preview.preview?.length > 0 && (
                <ul className="mt-1 space-y-0.5 max-h-32 overflow-auto">
                  {preview.preview.map((p, i) => <li key={i} className="text-slate-600">{p.phone} {p.name && `· ${p.name}`}</li>)}
                </ul>
              )}
            </div>
          )}

          <div className="pt-2"><Label>Template (optional)</Label>
            <Select value={form.template_key || 'none'} onValueChange={(v) => setForm({ ...form, template_key: v === 'none' ? '' : v })}>
              <SelectTrigger data-testid="wa-broadcast-template"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— None (send raw body) —</SelectItem>
                {templates.map(t => <SelectItem key={t.key} value={t.key}>{t.name || t.key}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {form.template_key ? (
            <>
              <TemplateContextFields
                template={selectedTemplate}
                context={context}
                onChange={setContext}
                testIdBase="wa-broadcast"
                autoFilledKeys={['name']}
              />
              {selectedTemplate && (
                <div className="rounded-lg border bg-white p-3" data-testid="wa-broadcast-preview-body">
                  <p className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Live preview <span className="lowercase">(per-recipient name will replace {'{name}'})</span></p>
                  <p className="text-sm whitespace-pre-wrap text-slate-700">{renderTemplatePreview(selectedTemplate.body, { ...context, name: '{name}' })}</p>
                </div>
              )}
            </>
          ) : (
            <div><Label>Message body</Label>
              <Textarea rows={5} value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} data-testid="wa-broadcast-body" />
            </div>
          )}
          <Button onClick={() => sendMut.mutate()} disabled={sendMut.isPending} data-testid="wa-broadcast-send">
            {sendMut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}<Megaphone className="h-4 w-4 mr-1" />Send Broadcast
          </Button>
          {result && (
            <div className="text-xs border rounded p-2 bg-slate-50">
              <p>{result.sent} sent · {result.failed} failed · {result.skipped} skipped</p>
              <p className="text-[10px] text-slate-400 mt-0.5">Broadcast id: <code>{result.broadcast_id}</code></p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Recent broadcasts ({broadcasts.length})</CardTitle></CardHeader>
        <CardContent className="p-0 max-h-[600px] overflow-auto">
          {bLoading ? (
            <div className="p-6 text-center"><Loader2 className="h-4 w-4 animate-spin inline" /></div>
          ) : broadcasts.length === 0 ? (
            <p className="p-10 text-center text-sm text-slate-400">No broadcasts yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="text-[10px] uppercase tracking-wide text-slate-500 border-b">
                <th className="text-left py-2 px-3">When</th>
                <th className="text-left py-2 px-3">Title</th>
                <th className="text-left py-2 px-3">Audience</th>
                <th className="text-right py-2 px-3">Sent</th>
                <th className="text-right py-2 px-3">Failed</th>
                <th className="text-right py-2 px-3">Skipped</th>
              </tr></thead>
              <tbody>
                {broadcasts.map(b => (
                  <tr key={b.id} className="border-b last:border-0" data-testid={`wa-broadcast-${b.id}`}>
                    <td className="py-2 px-3 text-[10px] text-slate-500 whitespace-nowrap">{new Date(b.created_at).toLocaleString()}</td>
                    <td className="py-2 px-3">{b.title}</td>
                    <td className="py-2 px-3 text-xs"><Badge variant="outline" className="text-[10px]">{b.audience_type}</Badge></td>
                    <td className="py-2 px-3 text-right text-emerald-700 font-medium">{b.sent || 0}</td>
                    <td className="py-2 px-3 text-right text-rose-600 font-medium">{b.failed || 0}</td>
                    <td className="py-2 px-3 text-right text-slate-500 font-medium">{b.skipped || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ============== OPT-OUTS (GDPR/POPIA compliance) ==============

function OptOutsCard() {
  const qc = useQueryClient();
  const [phone, setPhone] = useState('');
  const { data, isLoading } = useQuery({
    queryKey: ['wa-opt-outs'],
    queryFn: () => api('/api/admin/whatsapp/opt-outs'),
  });
  const optOuts = data?.opt_outs || [];

  const add = useMutation({
    mutationFn: () => api('/api/admin/whatsapp/opt-outs', { method: 'POST', body: JSON.stringify({ phone }) }),
    onSuccess: () => { toast.success('Opt-out added'); setPhone(''); qc.invalidateQueries({ queryKey: ['wa-opt-outs'] }); },
    onError: (e) => toast.error(e.message),
  });
  const remove = useMutation({
    mutationFn: (digits) => api(`/api/admin/whatsapp/opt-outs/${digits}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Re-subscribed'); qc.invalidateQueries({ queryKey: ['wa-opt-outs'] }); },
    onError: (e) => toast.error(e.message),
  });

  return (
    <Card data-testid="wa-opt-outs-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2"><AlertCircle className="h-4 w-4 text-rose-600" />Opt-outs ({optOuts.length})</CardTitle>
        <p className="text-[10px] text-slate-500 mt-1">Members who replied STOP / UNSUBSCRIBE are auto-filtered from every broadcast and direct send. You can also opt-out a phone manually.</p>
      </CardHeader>
      <CardContent>
        <div className="flex gap-2 mb-3">
          <Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+27 82 …" className="w-64" data-testid="wa-opt-out-phone" />
          <Button size="sm" onClick={() => add.mutate()} disabled={!phone || add.isPending} data-testid="wa-opt-out-add">Opt-out manually</Button>
        </div>
        {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : optOuts.length === 0 ? (
          <p className="text-xs text-slate-400">No opt-outs yet.</p>
        ) : (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2">
            {optOuts.map(o => (
              <div key={o.phone_digits} className="flex items-center justify-between px-3 py-2 rounded-lg bg-rose-50 border border-rose-200" data-testid={`wa-opt-out-${o.phone_digits}`}>
                <div>
                  <p className="text-xs font-mono">{o.phone}</p>
                  <p className="text-[10px] text-slate-500">{new Date(o.opt_out_at).toLocaleString()}</p>
                </div>
                <Button variant="ghost" size="icon" className="h-6 w-6 text-emerald-600" onClick={() => remove.mutate(o.phone_digits)} title="Re-subscribe" data-testid={`wa-opt-out-remove-${o.phone_digits}`}>
                  <RefreshCw className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// ============== DASHBOARD ==============

function DashboardTab() {
  const [days, setDays] = useState(7);
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['wa-stats', days],
    queryFn: () => api(`/api/admin/whatsapp/stats?days=${days}`),
  });

  if (isLoading) return <div className="p-12 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></div>;
  if (!data) return null;

  const t = data.totals || {};
  const daily = data.daily || [];
  const byModule = data.by_module || [];
  const topErrors = data.top_errors || [];
  const provider = data.provider_status || {};
  const successRate = t.total ? Math.round(((t.sent || 0) + (t.delivered || 0)) / t.total * 100) : 0;

  return (
    <div className="space-y-4" data-testid="wa-dashboard-tab">
      {/* Provider status banner */}
      {!provider.global_default_configured && (
        <Card className="border-amber-300 bg-amber-50" data-testid="wa-provider-warning">
          <CardContent className="py-3 flex items-center gap-2 text-sm">
            <AlertCircle className="h-4 w-4 text-amber-600" />
            <span><b>No global OpenWA settings configured.</b> Open the <code className="text-[10px] bg-white px-1 rounded">Settings</code> tab and create a global default to start sending. Every send below will fail until then.</span>
          </CardContent>
        </Card>
      )}

      {/* Range selector */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm">
          <span className="text-slate-500">Window</span>
          <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v, 10))}>
            <SelectTrigger className="w-28" data-testid="wa-stats-window"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1">24 hours</SelectItem>
              <SelectItem value="7">7 days</SelectItem>
              <SelectItem value="14">14 days</SelectItem>
              <SelectItem value="30">30 days</SelectItem>
              <SelectItem value="90">90 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button size="sm" variant="ghost" onClick={() => refetch()} data-testid="wa-stats-refresh">
          <RefreshCw className="h-4 w-4 mr-1" />Refresh
        </Button>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        <Tile label="Total" value={t.total || 0} tone="slate" testid="wa-kpi-total" />
        <Tile label="Sent" value={(t.sent || 0) + (t.queued || 0)} tone="emerald" testid="wa-kpi-sent" />
        <Tile label="Delivered" value={t.delivered || 0} tone="sky" testid="wa-kpi-delivered" />
        <Tile label="Failed" value={t.failed || 0} tone="rose" testid="wa-kpi-failed" />
        <Tile label="Skipped" value={t.skipped || 0} tone="amber" testid="wa-kpi-skipped" />
        <Tile label="Opt-outs" value={t.opt_outs || 0} tone="purple" testid="wa-kpi-opt-outs" />
      </div>

      {/* Success rate strip */}
      <Card data-testid="wa-success-rate-card">
        <CardContent className="py-3 flex items-center justify-between">
          <div>
            <p className="text-xs text-slate-500">Success rate</p>
            <p className="text-2xl font-bold">{successRate}%</p>
          </div>
          <div className="h-2 w-1/2 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500" style={{ width: `${successRate}%` }} />
          </div>
        </CardContent>
      </Card>

      {/* Daily chart */}
      <Card data-testid="wa-daily-chart">
        <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><BarChart3 className="h-4 w-4 text-emerald-700" />Daily activity</CardTitle></CardHeader>
        <CardContent>
          {daily.every(d => d.sent === 0 && d.failed === 0 && d.delivered === 0 && d.skipped === 0) ? (
            <p className="text-sm text-slate-400 text-center py-8">No WhatsApp activity in this window yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={daily}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="sent" stackId="a" fill="#10b981" name="Sent" />
                <Bar dataKey="delivered" stackId="a" fill="#0ea5e9" name="Delivered" />
                <Bar dataKey="failed" stackId="a" fill="#f43f5e" name="Failed" />
                <Bar dataKey="skipped" stackId="a" fill="#f59e0b" name="Skipped" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* By module + Top errors side by side */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card data-testid="wa-by-module-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><TrendingDown className="h-4 w-4 text-rose-600" />Top modules by volume</CardTitle>
            <p className="text-[10px] text-slate-500">Sorted by failure count, then total</p>
          </CardHeader>
          <CardContent className="p-0">
            {byModule.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-slate-400">No data.</p>
            ) : (
              <table className="w-full text-sm">
                <thead><tr className="text-[10px] uppercase tracking-wide text-slate-500 border-b">
                  <th className="text-left py-2 px-3">Module</th>
                  <th className="text-right py-2 px-3">Sent</th>
                  <th className="text-right py-2 px-3">Failed</th>
                  <th className="text-right py-2 px-3">Total</th>
                </tr></thead>
                <tbody>
                  {byModule.map(m => {
                    const fr = m.total ? Math.round(m.failed / m.total * 100) : 0;
                    return (
                      <tr key={m.module} className="border-b last:border-0" data-testid={`wa-module-${m.module}`}>
                        <td className="py-2 px-3 font-medium text-slate-700">
                          {m.module}
                          {fr >= 50 && m.total > 5 && <span className="ml-2 text-[9px] px-1 py-0.5 bg-rose-100 text-rose-700 rounded">{fr}% failing</span>}
                        </td>
                        <td className="py-2 px-3 text-right text-emerald-700">{m.sent + m.queued + m.delivered}</td>
                        <td className="py-2 px-3 text-right text-rose-700 font-medium">{m.failed}</td>
                        <td className="py-2 px-3 text-right text-slate-600 font-medium">{m.total}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        <Card data-testid="wa-top-errors-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><AlertCircle className="h-4 w-4 text-rose-600" />Top failure reasons</CardTitle>
            <p className="text-[10px] text-slate-500">Grouped failure messages from the engine</p>
          </CardHeader>
          <CardContent className="p-0">
            {topErrors.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-slate-400">No failures recorded.</p>
            ) : (
              <ul className="divide-y">
                {topErrors.map((e, i) => (
                  <li key={i} className="py-2 px-3 flex items-center justify-between gap-2" data-testid={`wa-error-${i}`}>
                    <span className="text-xs text-slate-700 truncate" title={e.error}>{e.error}</span>
                    <Badge variant="outline" className="text-[10px] bg-rose-50 text-rose-700 border-rose-200 whitespace-nowrap">{e.count}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

const DASH_TONES = {
  slate: 'bg-slate-50 border-slate-200 text-slate-900',
  emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
  sky: 'bg-sky-50 border-sky-200 text-sky-900',
  rose: 'bg-rose-50 border-rose-200 text-rose-900',
  amber: 'bg-amber-50 border-amber-200 text-amber-900',
  purple: 'bg-purple-50 border-purple-200 text-purple-900',
};
function Tile({ label, value, tone, testid }) {
  return (
    <div className={`px-3 py-3 rounded-xl border ${DASH_TONES[tone] || DASH_TONES.slate}`} data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wide opacity-70">{label}</p>
      <p className="text-xl font-bold">{Number(value || 0).toLocaleString()}</p>
    </div>
  );
}

